import React from 'react'
import { Heading, ContentfulRichText, Body } from '@superhi/design'

import { Wrapper, Code } from './styles'
import Hyperlink from './components/Hyperlink'
import LibraryPostBlockMedia, {
  LibraryPostBlockMedia as LibraryPostBlockMediaType,
} from './components/LibraryPostBlockMedia'
import LibraryPostBlockLetter, {
  LibraryPostBlockLetter as LibraryPostBlockLetterType,
} from './components/LibraryPostBlockLetter'
import LibraryPostBlockCode, {
  LibraryPostBlockCode as LibraryPostBlockCodeType,
} from './components/LibraryPostBlockCode'
import LibraryPostBlockEmbed, {
  LibraryPostBlockEmbed as LibraryPostBlockEmbedType,
} from './components/LibraryPostBlockEmbed'
import LibraryPostBlockCollapsible, {
  LibraryPostBlockCollapsible as LibraryPostBlockCollapsibleType,
} from './components/LibraryPostBlockCollapsible'
import Blockquote from './components/Blockquote'

type LibraryPostBlock =
  | LibraryPostBlockMediaType
  | LibraryPostBlockLetterType
  | LibraryPostBlockCodeType
  | LibraryPostBlockEmbedType
  | LibraryPostBlockCollapsibleType

const childrenToText = (children: React.ReactNode): string => {
  if (Array.isArray(children)) {
    return children.map(childrenToText).join('')
  }

  switch (typeof children) {
    case 'string': {
      return children
    }
    case 'object': {
      if (React.isValidElement(children)) {
        return childrenToText(children.props.children)
      }

      return ''
    }
    case 'bigint':
    case 'boolean':
    case 'number': {
      return `${children}`
    }

    default: {
      return ''
    }
  }
}

const NODE_OVERRIDES: React.ComponentProps<typeof ContentfulRichText>['nodeOverrides'] = {
  paragraph: (_, children) => <Body level="2">{children}</Body>,

  'heading-1': (_, children) => <Heading level="2">{childrenToText(children)}</Heading>,

  'heading-2': (_, children) => <Heading level="3">{childrenToText(children)}</Heading>,

  'heading-3': (_, children) => <Heading level="4">{childrenToText(children)}</Heading>,

  blockquote: (node) => <Blockquote>{node}</Blockquote>,

  hyperlink: (node, children) => <Hyperlink url={node.data.uri}>{children}</Hyperlink>,

  'embedded-asset-block': (block) => (
    <LibraryPostBlockMedia
      type={block.data.target.fields.file.contentType.includes('image') ? 'image' : 'video'}
      width={block.data.target.fields.file.details.image?.width}
      height={block.data.target.fields.file.details.image?.height}
      url={block.data.target.fields.file.url}
    />
  ),

  'embedded-entry-block': (data) => {
    const block = data as unknown as LibraryPostBlock

    switch (block.sys.contentType.sys.id) {
      case 'blogPostLetter': {
        const letterBlock = block as LibraryPostBlockLetterType

        return (
          <LibraryPostBlockLetter>{letterBlock.data.target.fields.body}</LibraryPostBlockLetter>
        )
      }

      case 'blogPostEmbed': {
        const embedBlock = block as LibraryPostBlockEmbedType

        return (
          <LibraryPostBlockEmbed caption={embedBlock.data.target.fields.caption}>
            {embedBlock.data.target.fields.code}
          </LibraryPostBlockEmbed>
        )
      }

      case 'blogPostMedia': {
        const mediaBlock = block as LibraryPostBlockMediaType

        return (
          <LibraryPostBlockMedia
            type={
              mediaBlock.data.target.fields.media.fields.file.contentType.includes('image')
                ? 'image'
                : 'video'
            }
            width={mediaBlock.data.target.fields.media.fields.file.details.image?.width}
            height={mediaBlock.data.target.fields.media.fields.file.details.image?.height}
            url={mediaBlock.data.target.fields.media.fields.file.url}
            caption={mediaBlock.data.target.fields.caption}
          />
        )
      }

      case 'libraryPostBlockCode': {
        const codeBlock = block as LibraryPostBlockCodeType

        return (
          <LibraryPostBlockCode language={codeBlock.data.target.fields.language}>
            {codeBlock.data.target.fields.content}
          </LibraryPostBlockCode>
        )
      }
      default: {
        return null
      }

      case 'libraryPostBlockCollapsible': {
        const collapsibleBlock = block as LibraryPostBlockCollapsibleType
        // return null
        return (
          <LibraryPostBlockCollapsible
            title={collapsibleBlock.data.target.fields.title}
            content={collapsibleBlock.data.target.fields.content}
          />
        )
      }
    }
  },
}

const MARK_OVERRIDES: React.ComponentProps<typeof ContentfulRichText>['markOverrides'] = {
  code: (children) => <Code>{children}</Code>,
}

type Props = {
  children: string
}

const ArticleBody = ({ children }: Props) => (
  <Wrapper>
    <ContentfulRichText nodeOverrides={NODE_OVERRIDES} markOverrides={MARK_OVERRIDES}>
      {children}
    </ContentfulRichText>
  </Wrapper>
)

export default ArticleBody
